import React from 'react';
import {
  Users, Layers, Box, Database, DatabaseBackup, DatabaseZap, Shield, ShieldAlert, ShieldBan, ShieldOff,
  ShieldX, Bug, Bot, Cookie, Server, Cloud, Globe, GlobeLock, Cpu, Zap,
  Activity, Anchor, Archive, Award, BarChart, HardDriveUpload, HardDriveDownload, Bell, Book,
  Briefcase, Calendar, Camera, CheckCircle, Clipboard, Code, Command, Compass, Copy,
  CreditCard, Download, Edit, ExternalLink, Eye, FileText,
  Filter, Flag, Folder, GitBranch, GitCommit, GitMerge, GitPullRequest, HardDrive, Hash,
  Heart, HelpCircle, Home, Image, Inbox, Info, Key, Link,
  Lock, LogIn, LogOut, Mail, Map, MapPin, Menu, MessageCircle,
  Monitor, Moon, MoreHorizontal, MoreVertical, MousePointer, Move, Navigation,
  Package, Paperclip, Pause, PenTool, Percent, Phone, PieChart, Play, Plus,
  Power, Printer, Radio, RefreshCcw, RefreshCw, Repeat, Rewind, RotateCcw, RotateCw, Rss,
  Save, Scissors, Search, Send, Settings, Share, Share2, ShoppingBag, ShoppingCart, Shuffle,
  Sidebar, SkipBack, SkipForward, Sliders, Smartphone, File, Star, StopCircle,
  Tablet, Tag, Terminal, Router, ThumbsDown, ThumbsUp,
  ToggleLeft, ToggleRight, Trash, Trash2, TrendingDown, TrendingUp, Truck, Tv,
  Type, Umbrella, Unlock, Upload, User, UserCheck, UserMinus, UserPlus,
  UserX, Users as UsersIcon, Video,
  X, XCircle, XSquare, Earth, ZapOff, ZoomIn, ZoomOut
} from "lucide-react";

// Base ICON_MAP from Lucide icons
export const ICON_MAP: Record<string, React.ElementType> = {
  users: Users, layers: Layers, box: Box, database: Database, server: Server, cloud: Cloud, globe: Globe, globelock: GlobeLock, cpu: Cpu, shield: Shield, zap: Zap,
  activity: Activity, databasebackup: DatabaseBackup, databasezap: DatabaseZap, anchor: Anchor, shieldoff: ShieldOff, archive: Archive, award: Award, barChart: BarChart, harddriveupload: HardDriveUpload, harddrivedownload: HardDriveDownload, bell: Bell, book: Book,
  briefcase: Briefcase, calendar: Calendar, camera: Camera, checkCircle: CheckCircle, clipboard: Clipboard, code: Code, command: Command, compass: Compass, copy: Copy,
  creditCard: CreditCard, shieldban: ShieldBan, shieldalert: ShieldAlert, download: Download, edit: Edit, externalLink: ExternalLink, eye: Eye, shieldx: ShieldX, fileText: FileText, cookie: Cookie,
  filter: Filter, flag: Flag, folder: Folder, gitBranch: GitBranch, gitCommit: GitCommit, gitMerge: GitMerge, gitPullRequest: GitPullRequest, hardDrive: HardDrive, hash: Hash,
heart: Heart, helpCircle: HelpCircle, home: Home, image: Image, inbox: Inbox, info: Info, key: Key, link: Link,
  lock: Lock, logIn: LogIn, logOut: LogOut, mail: Mail, map: Map, mapPin: MapPin, menu: Menu, messageCircle: MessageCircle,
  monitor: Monitor, moon: Moon, moreHorizontal: MoreHorizontal, moreVertical: MoreVertical, mousePointer: MousePointer, move: Move, navigation: Navigation,
  package: Package, paperclip: Paperclip, pause: Pause, penTool: PenTool, percent: Percent, phone: Phone, pieChart: PieChart, play: Play, plus: Plus,
  power: Power, printer: Printer, radio: Radio, refreshCcw: RefreshCcw, refreshCw: RefreshCw, repeat: Repeat, rewind: Rewind, rotateCcw: RotateCcw, rotateCw: RotateCw, rss: Rss,
  save: Save, scissors: Scissors, search: Search, send: Send, settings: Settings, share: Share, share2: Share2, shoppingBag: ShoppingBag, shoppingCart: ShoppingCart, shuffle: Shuffle,
  sidebar: Sidebar, skipBack: SkipBack, skipForward: SkipForward, sliders: Sliders, smartphone: Smartphone, file: File, star: Star, stopCircle: StopCircle,
  tablet: Tablet, tag: Tag, terminal: Terminal, router: Router, thumbsDown: ThumbsDown, thumbsUp: ThumbsUp,
  toggleLeft: ToggleLeft, toggleRight: ToggleRight, trash: Trash, trash2: Trash2, trendingDown: TrendingDown, trendingUp: TrendingUp, truck: Truck, tv: Tv,
  type: Type, umbrella: Umbrella, unlock: Unlock, upload: Upload, user: User, userCheck: UserCheck, userMinus: UserMinus, userPlus: UserPlus,
  userX: UserX, usersIcon: UsersIcon, video: Video, bug: Bug, bot: Bot,
  x: X, xCircle: XCircle, xOctagon: XSquare, earth: Earth, zapOff: ZapOff, zoomIn: ZoomIn, zoomOut: ZoomOut
};

// Custom icon type for the metarouter icon
export interface CustomSvgIconProps {
  size?: number;
}

// MetaRouter SVG icon component
export const MetaRouterIcon: React.FC<CustomSvgIconProps> = ({ size = 24 }) => (
  <svg height={size} width={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M15.7758 5.10115L9.52722 14.3738C9.10118 15.002 8.22069 15.1932 7.56742 14.7835C7.2834 14.606 7.07037 14.3328 6.98517 14.0324C6.43131 12.2434 5.97687 11.1236 5.63603 10.7139C5.3094 10.3042 4.3153 9.60775 2.68214 8.63814C2.00047 8.2148 1.80165 7.38176 2.2135 6.73991C2.39811 6.45313 2.69634 6.23463 3.02297 6.13903L14.1853 3.05269C14.938 2.84785 15.7332 3.25754 15.9463 3.98133C16.0599 4.36371 15.9889 4.7734 15.7758 5.10115Z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Extended icon map including the custom MetaRouterIcon
export const EXTENDED_ICON_MAP: Record<string, React.ElementType> = {
  metarouter: MetaRouterIcon,
  ...ICON_MAP
};

// Special case mappings for specific icons
const SPECIAL_ICON_LABELS: Record<string, string> = {
  'metarouter': 'MetaRouter',
  // Add any other special cases here
};

/**
 * Intelligently formats an icon key into a readable label
 * Handles various naming patterns:
 * - lowercase: "shieldoff" -> "Shield Off"
 * - camelCase: "hardDrive" -> "Hard Drive"
 * - lowercase with numbers: "share2" -> "Share 2"
 * - compound words: "databasezap" -> "Database Zap"
 */
const formatIconLabel = (key: string): string => {
  // Check for special cases first
  if (SPECIAL_ICON_LABELS[key.toLowerCase()]) {
    return SPECIAL_ICON_LABELS[key.toLowerCase()];
  }
  
  // Step 1: Split by existing uppercase letters (handles camelCase)
  let parts = key.split(/(?=[A-Z])/).map(part => part.toLowerCase());
  
  // Step 2: For all-lowercase keys, try to identify common compound words
  if (parts.length === 1) {
    // Common prefixes to check for
    const commonPrefixes = [
      'shield', 'database', 'hard', 'drive', 'git', 'globe', 'zap', 
      'refresh', 'rotate', 'shopping', 'toggle', 'trending', 'user', 'file'
    ];
    
    // Try to split by common prefixes
    for (const prefix of commonPrefixes) {
      if (key.toLowerCase().startsWith(prefix) && key.toLowerCase() !== prefix) {
        const remainder = key.slice(prefix.length);
        parts = [prefix, remainder];
        break;
      }
    }
    
    // If still one part, try to split by numbers
    if (parts.length === 1) {
      parts = key.split(/(?=\d)/).filter(Boolean);
    }
  }
  
  // Step 3: Capitalize each part and join with spaces
  return parts
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// Icon options for the IconSelector component
export const ICON_OPTIONS = Object.keys(EXTENDED_ICON_MAP).map(key => ({
  value: key,
  label: formatIconLabel(key),
  icon: EXTENDED_ICON_MAP[key]
}));
